<template>
  <div class="comet-explorer">
    <div class="header">
      <h1>Scottish FA Comet Data Explorer</h1>
      <p class="subtitle">Test interface for exploring Comet player data</p>
    </div>

    <div class="status-card" :class="statusClass">
      <h3>Authentication Status</h3>
      <div v-if="loading" class="loading">
        <span>Loading...</span>
      </div>
      <div v-else>
        <p><strong>Status:</strong> {{ authStatus.status || "Unknown" }}</p>
        <p>
          <strong>Login Required:</strong>
          {{ authStatus.loginRequired ? "Yes" : "No" }}
        </p>
        <p>
          <strong>Has Cookies:</strong>
          {{ authStatus.hasCookies ? "Yes" : "No" }}
        </p>
        <p v-if="authStatus.status && authStatus.status.timestamp">
          <strong>Last Updated:</strong>
          {{ formatDate(authStatus.status.timestamp) }}
        </p>
      </div>
    </div>

    <div class="actions">
      <button @click="authenticate" :disabled="loading" class="btn btn-primary">
        {{ loading ? "Processing..." : "Authenticate with Comet" }}
      </button>

      <button
        @click="checkStatus"
        :disabled="loading"
        class="btn btn-secondary"
      >
        Check Status
      </button>

      <button
        @click="exploreData"
        :disabled="loading || !authStatus.hasCookies"
        class="btn btn-success"
      >
        Explore Player Data
      </button>
    </div>

    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>

    <div v-if="authOutput && authOutput.length > 0" class="output-section">
      <h3>Authentication Output</h3>
      <div class="output-box">
        <pre>{{ authOutput.join("\n") }}</pre>
      </div>
    </div>

    <div v-if="playerData" class="data-section">
      <h3>Extracted Data Preview</h3>

      <div class="data-summary">
        <div class="summary-card">
          <h4>Summary</h4>
          <p>
            <strong>Page Title:</strong>
            {{ playerData.pageData?.pageTitle || "N/A" }}
          </p>
          <p>
            <strong>Current URL:</strong> {{ playerData.currentUrl || "N/A" }}
          </p>
          <p>
            <strong>Tables Found:</strong>
            {{ playerData.pageData?.tables?.length || 0 }}
          </p>
          <p>
            <strong>Forms Found:</strong>
            {{ playerData.pageData?.forms?.length || 0 }}
          </p>
          <p>
            <strong>API Requests:</strong>
            {{ playerData.apiRequests?.length || 0 }}
          </p>
        </div>
      </div>

      <div
        v-if="playerData.apiRequests && playerData.apiRequests.length > 0"
        class="api-requests"
      >
        <h4>API Requests Detected</h4>
        <div class="request-list">
          <div
            v-for="(request, index) in playerData.apiRequests"
            :key="index"
            class="request-item"
          >
            <p>
              <strong>{{ request.method }}:</strong> {{ request.url }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="
          playerData.pageData?.tables && playerData.pageData.tables.length > 0
        "
        class="tables-section"
      >
        <h4>Tables Found on Page</h4>
        <div
          v-for="(table, tableIndex) in playerData.pageData.tables"
          :key="tableIndex"
          class="table-preview"
        >
          <h5>Table {{ tableIndex + 1 }} ({{ table.rows.length }} rows)</h5>
          <div class="table-scroll">
            <table>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in table.rows.slice(0, 10)"
                  :key="rowIndex"
                >
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="table.rows.length > 10" class="more-rows">
              ... and {{ table.rows.length - 10 }} more rows
            </p>
          </div>
        </div>
      </div>

      <div class="raw-data">
        <h4>Full Raw Data (JSON)</h4>
        <button @click="toggleRawData" class="btn btn-sm">
          {{ showRawData ? "Hide" : "Show" }} Raw Data
        </button>
        <div v-if="showRawData" class="raw-data-box">
          <pre>{{ JSON.stringify(playerData, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CometDataExplorer",
  data() {
    return {
      loading: false,
      message: "",
      messageType: "",
      authStatus: {},
      authOutput: [],
      playerData: null,
      showRawData: false,
      apiUrl: process.env.VUE_APP_API_URL || "http://www.localhost:8888",
    };
  },
  computed: {
    statusClass() {
      if (this.authStatus.status?.status === "success") {
        return "status-success";
      } else if (this.authStatus.status?.status === "error") {
        return "status-error";
      }
      return "status-unknown";
    },
  },
  mounted() {
    this.checkStatus();
  },
  methods: {
    async authenticate() {
      this.loading = true;
      this.message = "";
      this.authOutput = [];

      try {
        const response = await axios.get(
          `${this.apiUrl}/back-end-endpoint-comet-data.php?action=authenticateComet`,
        );

        if (response.data.success) {
          this.message =
            "Authentication successful! You can now explore player data.";
          this.messageType = "success";
          this.authOutput = response.data.output || [];
          this.authStatus = {
            status: response.data.status,
            hasCookies: true,
            loginRequired: false,
          };
        } else {
          this.message = `Authentication failed: ${response.data.message}`;
          this.messageType = "error";
          this.authOutput = response.data.output || [];
        }
      } catch (error) {
        this.message = `Error: ${error.response?.data?.message || error.message}`;
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },

    async checkStatus() {
      this.loading = true;
      this.message = "";

      try {
        const response = await axios.get(
          `${this.apiUrl}/back-end-endpoint-comet-data.php?action=getStatus`,
        );

        if (response.data.success) {
          this.authStatus = response.data;
          this.message = "Status updated";
          this.messageType = "info";
        }
      } catch (error) {
        this.message = `Error checking status: ${error.message}`;
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },

    async exploreData() {
      this.loading = true;
      this.message = "";
      this.playerData = null;

      try {
        const response = await axios.get(
          `${this.apiUrl}/back-end-endpoint-comet-data.php?action=exploreData`,
        );

        if (response.data.success) {
          this.playerData = response.data.data;
          this.message = "Data retrieved successfully! Review the data below.";
          this.messageType = "success";
        } else {
          this.message = response.data.message;
          this.messageType = "error";

          if (response.data.loginRequired) {
            this.message += " Please authenticate first.";
          }
        }
      } catch (error) {
        this.message = `Error: ${error.response?.data?.message || error.message}`;
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },

    toggleRawData() {
      this.showRawData = !this.showRawData;
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
  },
};
</script>

<style scoped>
.comet-explorer {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.subtitle {
  color: #7f8c8d;
  font-size: 14px;
}

.status-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #95a5a6;
}

.status-success {
  border-left-color: #27ae60;
}

.status-error {
  border-left-color: #e74c3c;
}

.status-unknown {
  border-left-color: #f39c12;
}

.status-card h3 {
  margin-top: 0;
  color: #2c3e50;
}

.status-card p {
  margin: 8px 0;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
}

.actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-secondary {
  background: #95a5a6;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #7f8c8d;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #229954;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 12px;
}

.message {
  padding: 12px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.output-section,
.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.output-section h3,
.data-section h3 {
  margin-top: 0;
  color: #2c3e50;
}

.output-box,
.raw-data-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.output-box pre,
.raw-data-box pre {
  margin: 0;
  font-family: "Courier New", monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.data-summary {
  margin-bottom: 20px;
}

.summary-card {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.summary-card h4 {
  margin-top: 0;
  color: #2c3e50;
}

.summary-card p {
  margin: 8px 0;
}

.api-requests,
.tables-section,
.raw-data {
  margin-top: 20px;
}

.api-requests h4,
.tables-section h4,
.raw-data h4 {
  color: #2c3e50;
  margin-bottom: 15px;
}

.request-list {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.request-item {
  padding: 10px;
  border-bottom: 1px solid #dee2e6;
}

.request-item:last-child {
  border-bottom: none;
}

.request-item p {
  margin: 0;
  font-family: "Courier New", monospace;
  font-size: 12px;
}

.table-preview {
  margin-bottom: 20px;
}

.table-preview h5 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.table-scroll {
  overflow-x: auto;
}

.table-scroll table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.table-scroll table td {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  font-size: 12px;
}

.table-scroll table tr:nth-child(even) {
  background: #f8f9fa;
}

.more-rows {
  text-align: center;
  color: #7f8c8d;
  font-style: italic;
  margin-top: 10px;
}

.raw-data button {
  margin-bottom: 10px;
}
</style>
